/**
 * Consentimiento Form Dialog - Create Informed Consent
 *
 * Formulario simple (sin wizard):
 * - Seleccionar tipo de consentimiento
 * - Descargar plantilla .docx
 * - Datos del firmante
 * - Subir documento escaneado (PDF/JPG/PNG)
 * - Guardar
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2, Upload, Download, FileText } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useApiError } from "@/hooks/use-api-error";
import {
  useCreateConsentimiento,
  descargarPlantilla,
  TIPOS_CONSENTIMIENTO_VALUES,
  RELACIONES_FIRMANTE_VALUES,
  type TipoConsentimiento,
  type RelacionFirmante,
} from "@/hooks/use-consentimientos";

interface ConsentimientoFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pacienteId: number;
  pacienteNombre: string;
  onSuccess: () => void;
}

// Zod schema
const consentimientoSchema = z
  .object({
    tipo: z.enum([
      "PANEL_DROGAS",
      "HIV",
      "ANTIGENO_SARS_COV2",
      "COVID19_ANTICUERPOS",
      "PCR_RT_COVID",
      "ENTREVISTA_DONANTE",
    ] as const),
    firmante: z.string().min(3, "consentimientos.validation.signerRequired"),
    relacion_firmante: z.enum([
      "PACIENTE",
      "TUTOR",
      "REPRESENTANTE_LEGAL",
      "PADRE_MADRE",
      "OTRO",
    ] as const),
    documento_firmante: z.string().optional(),
    aceptado: z.boolean(),
    observaciones: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.relacion_firmante !== "PACIENTE") {
        return !!data.documento_firmante && data.documento_firmante.length >= 1;
      }
      return true;
    },
    {
      message: "consentimientos.validation.signerDocumentRequired",
      path: ["documento_firmante"],
    }
  );

type FormData = z.infer<typeof consentimientoSchema>;

const VALID_FILE_TYPES = ["application/pdf", "image/jpeg", "image/png"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

export function ConsentimientoFormDialog({
  open,
  onOpenChange,
  pacienteId,
  pacienteNombre,
  onSuccess,
}: ConsentimientoFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);
  const [documentoEscaneado, setDocumentoEscaneado] = useState<File | null>(null);

  const createMutation = useCreateConsentimiento({
    onSuccess: () => {
      onSuccess();
      onOpenChange(false);
    },
    onError: (err) => setError(getErrorMessage(err)),
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(consentimientoSchema),
    defaultValues: {
      tipo: "PANEL_DROGAS",
      firmante: "",
      relacion_firmante: "PACIENTE",
      documento_firmante: "",
      aceptado: true,
      observaciones: "",
    },
  });

  const tipo = watch("tipo");
  const relacionFirmante = watch("relacion_firmante");
  const aceptado = watch("aceptado");

  // Reset on open
  useEffect(() => {
    if (open) {
      reset({
        tipo: "PANEL_DROGAS",
        firmante: pacienteNombre,
        relacion_firmante: "PACIENTE",
        documento_firmante: "",
        aceptado: true,
        observaciones: "",
      });
      setDocumentoEscaneado(null);
      setError(null);
    }
  }, [open, pacienteNombre, reset]);

  // Update firmante when relacion changes
  useEffect(() => {
    if (relacionFirmante === "PACIENTE") {
      setValue("firmante", pacienteNombre);
      setValue("documento_firmante", "");
    } else {
      setValue("firmante", "");
    }
  }, [relacionFirmante, pacienteNombre, setValue]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!VALID_FILE_TYPES.includes(file.type)) {
      setError(
        t(
          "consentimientos.validation.invalidFileType",
          "Tipo de archivo no válido. Use PDF, JPG o PNG."
        )
      );
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(
        t("consentimientos.validation.fileTooLarge", "El archivo excede el tamaño máximo de 10 MB.")
      );
      return;
    }
    setDocumentoEscaneado(file);
    setError(null);
  };

  const onSubmit = (data: FormData) => {
    setError(null);

    if (!documentoEscaneado) {
      setError(
        t(
          "consentimientos.validation.documentRequired",
          "Debe subir el documento escaneado firmado."
        )
      );
      return;
    }

    createMutation.mutate({
      paciente: pacienteId,
      tipo: data.tipo,
      documento_escaneado: documentoEscaneado,
      firmante: data.firmante,
      relacion_firmante: data.relacion_firmante,
      documento_firmante: data.documento_firmante || undefined,
      aceptado: data.aceptado,
      observaciones: data.observaciones || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("consentimientos.newConsent", "Nuevo Consentimiento")}</DialogTitle>
          <DialogDescription>
            {t("consentimientos.newConsentDesc", "Registrar consentimiento informado para")}{" "}
            <span className="font-medium">{pacienteNombre}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          {/* Tipo de Consentimiento + Descargar Plantilla */}
          <div className="space-y-2">
            <Label>{t("consentimientos.fields.type", "Tipo de Consentimiento")}</Label>
            <div className="flex gap-2">
              <Select
                value={tipo}
                onValueChange={(value) => setValue("tipo", value as TipoConsentimiento)}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CONSENTIMIENTO_VALUES.map((value) => (
                    <SelectItem key={value} value={value}>
                      {t(`consentimientos.tipos.${value}`, value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  descargarPlantilla(tipo).catch(() => {
                    setError(
                      t(
                        "consentimientos.validation.downloadError",
                        "Error al descargar la plantilla."
                      )
                    );
                  });
                }}
              >
                <Download className="h-4 w-4 mr-1" />
                {t("consentimientos.downloadTemplate", "Plantilla")}
              </Button>
            </div>
          </div>

          {/* Relación del Firmante */}
          <div className="space-y-2">
            <Label>{t("consentimientos.fields.signerRelation", "¿Quién firma?")}</Label>
            <Select
              value={relacionFirmante}
              onValueChange={(v) => setValue("relacion_firmante", v as RelacionFirmante)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RELACIONES_FIRMANTE_VALUES.map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`consentimientos.relaciones.${value}`, value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Nombre y Documento del Firmante (si no es el paciente) */}
          {relacionFirmante !== "PACIENTE" && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firmante">
                  {t("consentimientos.fields.signerName", "Nombre del Firmante")}
                </Label>
                <Input
                  id="firmante"
                  placeholder="Nombre completo"
                  className="placeholder:text-muted-foreground/50"
                  {...register("firmante")}
                />
                {errors.firmante && (
                  <p className="text-sm text-destructive">{t(errors.firmante.message as string)}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="documento_firmante">
                  {t("consentimientos.fields.signerDocument", "Documento del Firmante")}
                </Label>
                <Input
                  id="documento_firmante"
                  placeholder="Número de documento"
                  className="placeholder:text-muted-foreground/50"
                  {...register("documento_firmante")}
                />
                {errors.documento_firmante && (
                  <p className="text-sm text-destructive">
                    {t(errors.documento_firmante.message as string)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Observaciones */}
          <div className="space-y-2">
            <Label htmlFor="observaciones">
              {t("consentimientos.fields.observations", "Observaciones")}
            </Label>
            <Textarea
              id="observaciones"
              placeholder={t(
                "consentimientos.fields.observationsPlaceholder",
                "Notas adicionales..."
              )}
              className="placeholder:text-muted-foreground/50"
              rows={2}
              {...register("observaciones")}
            />
          </div>

          {/* Upload documento escaneado */}
          <div className="space-y-2">
            <Label>
              {t("consentimientos.fields.scannedDocument", "Documento Escaneado Firmado")}
            </Label>
            <label
              htmlFor="documento_file"
              className="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer bg-muted/50 hover:bg-muted transition-colors"
            >
              <div className="flex flex-col items-center justify-center py-4">
                {documentoEscaneado ? (
                  <>
                    <FileText className="w-6 h-6 mb-1 text-primary" />
                    <p className="text-sm font-medium">{documentoEscaneado.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(documentoEscaneado.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </>
                ) : (
                  <>
                    <Upload className="w-6 h-6 mb-1 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {t("consentimientos.uploadDocument", "Subir documento firmado")}
                    </p>
                    <p className="text-xs text-muted-foreground">PDF, JPG o PNG (máx. 10MB)</p>
                  </>
                )}
              </div>
              <input
                id="documento_file"
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
            </label>
          </div>

          {/* Aceptado */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div className="space-y-0.5">
              <Label htmlFor="aceptado">
                {t("consentimientos.fields.accepted", "Consentimiento Aceptado")}
              </Label>
              <p className="text-sm text-muted-foreground">
                {t("consentimientos.fields.acceptedDesc", "El paciente acepta el procedimiento")}
              </p>
            </div>
            <Switch
              id="aceptado"
              checked={aceptado}
              onCheckedChange={(checked) => setValue("aceptado", checked)}
            />
          </div>

          {/* Footer */}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel", "Cancelar")}
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("consentimientos.saveConsent", "Guardar Consentimiento")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
